/**
 * Chat greeting: a small card above the chat bubble — "Hi Zolo 👋 …" — that opens the chat.
 *
 * Where the name comes from, in order: a signed-in customer (window.__mcpCustomerFirstName,
 * set in theme.liquid), a `?fn=` / `?first_name=` parameter on the link that brought them here
 * (email campaigns), or a first name they typed into one of our own forms on an earlier visit
 * (kept in this browser's localStorage, with a "Not you?" to forget it). A browser never tells
 * a site the name on its Chrome profile, so there is no fourth source. Without a name it says
 * "Hi there".
 *
 * Opening the chat: Shopify Inbox's <shopify-chat> element when it is on the page (show()),
 * otherwise the theme's own chat trigger (Tawk), otherwise the contact page.
 */
(() => {
  const cfg = window.__mcpGreeting;
  if (!cfg || !cfg.enabled || document.querySelector('.mcp-greeting')) return;
  if (document.querySelector('.product-information, .cart-page')) return; // not over the buy box or the cart

  const NAME_KEY = 'mcp:first-name';
  const SNOOZE_KEY = 'mcp:greeting-snoozed-until';
  const SHOWN_KEY = 'mcp:greeting-shown';
  const store = {
    get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} },
    del: (k) => { try { localStorage.removeItem(k); } catch (e) {} },
  };
  const session = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };

  // Only something that reads as a first name: letters (any script), apostrophes, hyphens, ≤ 20 chars.
  function cleanName(raw) {
    if (!raw) return '';
    const first = String(raw).trim().split(/\s+/)[0] || '';
    if (!/^[\p{L}'’-]{2,20}$/u.test(first)) return '';
    return first.charAt(0).toUpperCase() + first.slice(1);
  }

  // Remember a name the visitor gives us on any of our forms (capture phase, so it runs even
  // though those forms submit over fetch and stop the default).
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const field = form.querySelector('input[name="firstName"], input[name="first_name"], input[name="name"], input[name="contact[name]"]');
    const name = cleanName(field && field.value);
    if (name) store.set(NAME_KEY, name);
  }, true);

  // A name on the link that brought them here (email campaigns): keep it, then take it off the URL.
  try {
    const url = new URL(location.href);
    const fromLink = cleanName(url.searchParams.get('fn') || url.searchParams.get('first_name'));
    if (fromLink) {
      store.set(NAME_KEY, fromLink);
      url.searchParams.delete('fn'); url.searchParams.delete('first_name');
      history.replaceState(history.state, '', url.pathname + (url.search || '') + url.hash);
    }
  } catch (e) {}

  const name = cleanName(window.__mcpCustomerFirstName) || store.get(NAME_KEY) || '';

  function openChat() {
    const inbox = document.querySelector('shopify-chat');
    if (inbox && typeof inbox.show === 'function') { inbox.show(); return; }
    const trigger = document.querySelector('[data-open-chat], a[href$="#chat"]');
    if (trigger) { trigger.click(); return; }
    location.href = cfg.chatHref;
  }

  function build() {
    const box = document.createElement('div');
    box.className = 'mcp-greeting';
    box.setAttribute('role', 'region');
    box.setAttribute('aria-label', 'Chat invitation');

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'mcp-greeting__close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';

    const text = document.createElement('p');
    text.className = 'mcp-greeting__text';
    text.textContent = String(cfg.text || '').replace('[name]', name || cfg.fallback || 'there');

    const go = document.createElement('button');
    go.type = 'button';
    go.className = 'mcp-greeting__go';
    go.textContent = cfg.button || 'Chat with us';

    box.append(close, text, go);

    if (name && !window.__mcpCustomerFirstName) {
      const notYou = document.createElement('button');
      notYou.type = 'button';
      notYou.className = 'mcp-greeting__forget';
      notYou.textContent = 'Not ' + name + '?';
      notYou.addEventListener('click', () => { store.del(NAME_KEY); text.textContent = String(cfg.text || '').replace('[name]', cfg.fallback || 'there'); notYou.remove(); });
      box.appendChild(notYou);
    }

    const hide = () => { box.classList.remove('is-visible'); setTimeout(() => box.remove(), 300); };
    close.addEventListener('click', () => {
      hide();
      store.set(SNOOZE_KEY, String(Date.now() + (Number(cfg.snoozeDays) || 7) * 86400000));
    });
    go.addEventListener('click', () => { hide(); session.set(SHOWN_KEY, '1'); openChat(); });
    document.body.appendChild(box);
    requestAnimationFrame(() => requestAnimationFrame(() => box.classList.add('is-visible')));
    session.set(SHOWN_KEY, '1');
  }

  function eligible() {
    if (session.get(SHOWN_KEY)) return false;
    const until = Number(store.get(SNOOZE_KEY) || 0);
    if (until > Date.now()) return false;
    const inbox = document.querySelector('shopify-chat');
    if (inbox && inbox.hasAttribute('open')) return false;
    return true;
  }

  let done = false;
  function maybeShow() {
    if (done) return;
    done = true;
    if (eligible()) build();
  }
  setTimeout(maybeShow, Math.max(1, Number(cfg.delay) || 6) * 1000);
  addEventListener('scroll', () => { if (scrollY > innerHeight * 0.5) maybeShow(); }, { passive: true });
})();
